<template>
    <section>
        <!-- Header  -->
        <div class="w-full flex flex-row gap-6 items-center justify-center">
            <div>
                <Button icon="pi pi-chevron-left" size="small" @click="changeDate(-1)" />
            </div>
            <div>
                <h2>{{ calendarTitle }}</h2>
            </div>
            <div>
                <Button icon="pi pi-chevron-right" size="small" @click="changeDate(1)" />
            </div>
        </div>

        <!-- Filters -->
        <div class="w-full flex flex-row gap-6 items-center justify-center my-4">
            <FloatLabel class="w-full md:w-56" variant="on">
                <Select v-model="selectedHq" :options="headquartersOptions" optionLabel="name" optionValue="id"
                    class="w-full md:w-56" @change="onHqChange()"/>
                <label for="on_label">Sede</label>
            </FloatLabel>

            <FloatLabel class="w-full md:w-56" variant="on">
                <Select v-model="selectedRoom" :options="roomOptions" optionLabel="name" optionValue="id"
                    class="w-full md:w-56" @change="onRoomSelectChange" :disabled="selectedHq == null"/>
                <label for="on_label">Salas</label>
            </FloatLabel>

        </div>

        <!-- Data -->
        <section class="table-container overflow-x-auto overflow-y-auto border border-gray-400 rounded-lg">
            <table>
                <thead>
                    <tr>
                        <th class="sticky-col">Data</th>
                        <th class="whitespace-nowrap" :colspan="room.length" v-for="(data, dIndex) in calendarDates" :key="dIndex">
                            {{ data['text'] }}
                        </th>
                    </tr>
                    <tr>
                        <th class="sticky-col">Salas</th>
                        <template v-for="(data, dIndex) in calendarDates" :key="dIndex">
                            <th class="whitespace-nowrap" v-for="(data, rIndex) in room" :key="rIndex">{{ data['name'] }}</th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(hour, hIndex) in calendarHours" :key="hIndex">
                        <td class="sticky-col">{{ hour }}</td>
                        <template v-for="(date, dIndex) in calendarDates" :key="dIndex">
                            <td v-for="(room, rIndex) in room" :key="rIndex" :class="formatDate(date.date, hour) < new Date() ? 'past-time' : ''"
                                @dblclick="actionEvent(date.date, hour, room)">
                                <div v-tooltip.top.focus="{ value: getEvent(date.date, hour, room.id)?.author, hideDelay: 100, autoHide: true }" tabindex="1" class="event" v-if="getEvent(date.date, hour, room.id)" :class="{ 'event-others': getEvent(date.date, hour, room.id)?.author?.toLowerCase() === 'indisponível' }">
                                    <span>
                                        {{ formatAuthor(getEvent(date.date, hour, room.id).author) }}
                                    </span>
                                </div>
                                <div v-else-if="isSlotBlocked(date.date, hour, room)" class="event event-others">
                                    <span>🔒 Bloqueado</span>
                                </div>
                                <!-- Marker -->
                                <div class="marker" v-if="showMarker(date.date, hour)"
                                    :class="{ 'marker-circle': rIndex === 0 }" :style="{ top: markerPosition }">
                                </div>
                            </td>
                        </template>
                    </tr>
                </tbody>
            </table>
        </section>
    </section>
    <Dialog  :visible="editLocationVisible" :modal="true" @update:visible="editLocationVisible = $event" header="Editar Reserva" style="width: 55rem;">
        <EditLocation v-if="editLocationVisible" @update:dialogIsVisible="editLocationVisible = $event" :reservationId="selectedReservationId" />
    </Dialog>
    <Dialog :visible="createLocationVisible" :modal="true" @update:visible="createLocationVisible = $event" header="Criar Reserva" style="width: 40rem;">
        <CreateLocation v-if="createLocationVisible" @update:dialogIsVisible="createLocationVisible = $event" :date="selectedDate" :hour="selectedHour" :roomId="selectedRoomId" :isDialog="true" />
    </Dialog>

</template>

<script>
import { startOfWeek, addDays, format, parseISO } from 'date-fns';
import { Button, Dialog, FloatLabel, MultiSelect, Select } from 'primevue';

import HeadquartersService from '@/services/headquartersService.js';
import EditLocation from './EditLocation.vue';
import CreateLocation from './CreateLocation.vue';

export default {
    name: 'Calendar',
    components: {
        Button,
        Select,
        MultiSelect,
        FloatLabel,
        Dialog,
        EditLocation,
        CreateLocation
    },
    emits: ['update:selected-hq', 'update:selected-rooms', 'update:calendarDate'],
    props: {
        /* week or day */
        calendarMode: String,
        calendarEvents: Array,
        userData: Object,
        // optional: parent-controlled date (YYYY-MM-DD or Date)
        controlDate: [String, Date],
        controlHq: [Number, String],
        blockedDates: {
            type: Array,
            default: () => [], // [{ date: 'dd/MM/yyyy', hqIds: [ids]|null, reason }]
        },
    },
    computed: {
        markerPosition() {
            return `${(this.currentMinute / 60) * 100}%`
        }
    },
    data() {
        return {
            isStaff: this.$store.state.accessLevel == 'admin' || this.$store.state.accessLevel == 'secretary',

            currentMinute: new Date().getMinutes(),

            // Calendar options
            calendarDate: null,
            calendarTitle: null,
            calendarDates: null,
            calendarHours: null,

            // This room variables is used to build html data, it's different of the roomOptions (filter only)
            room: [
                // Example only
                // {
                //     id: 1,
                //     name: 'Sala 01',
                //     color: '',
                //     hqId: 1,
                // },
            ],

            // Filters
            selectedHq: null,
            headquartersOptions: [],

            // If parent requests an HQ before the list is loaded, store it here
            pendingControlHq: null,

            selectedRoom: null,
            roomOptions: [
                // Example only
                // {
                //     id: 1,
                //     name: 'Sala 01',
                //     color: ''
                // }
            ],
            editLocationVisible: false,
            selectedReservationId: null,
            createLocationVisible: false,
            selectedDate: null,
            selectedHour: null,
            selectedRoomId: null,
        }
    },
    methods: {
                formatAuthor(name) {
                    if (!name || typeof name !== 'string') return '';
                    // Keep Indisponível unchanged
                    if (name.toLowerCase() === 'indisponível')
                    return 'Indisponível';

                    const parts = name.trim().split(/\s+/);
                    if (parts.length === 1) return parts[0];

                    const first = parts[0];
                    const lastInitial = parts[parts.length - 1].charAt(0);
                    return `${first} ${lastInitial}.`;
                },
        // Listeners methods (when user change something)
        onRoomSelectChange(event) {
            this.room = this.roomOptions.filter((room) => this.selectedRoom == room.id);
            this.$emit('update:selected-rooms', this.selectedRoom);
        },
        onHqChange(){
            const hqData = this.headquartersOptions.find((hq) => hq.id === this.selectedHq);

            this.roomOptions = hqData.rooms.map((roomObject) => {
                return {
                    ...roomObject,
                    hqId: hqData.id
                }
            });
            this.room = hqData.rooms.map((roomObject) => {
                return {
                    ...roomObject,
                    hqId: hqData.id
                }
            });

            this.$emit('update:selected-hq', this.selectedHq);
        },
        // General methods
        getEvent(date, hour, roomId) {
            return this.calendarEvents.find(event => event.date === date && event.hour === hour && event.roomId === roomId);
        },
        getHqScheduleForDate(date, hqId) {
            const headquarters = this.headquartersOptions.find(hq => hq.id === hqId);
            if (!headquarters?.schedules?.length) return null;

            const [day, month, year] = date.split('/');
            const targetDate = new Date(year, month - 1, day);
            const dayOfWeek = targetDate.getDay();

            if (dayOfWeek === 0) {
                return null;
            }

            return headquarters.schedules.find(schedule => Number(schedule.dayOfWeek) === dayOfWeek) ?? null;
        },
        isDayBlockedForHq(date, hqId) {
            const blockedEntry = this.blockedDates.find(entry => entry.date === date);
            if (!blockedEntry) return false;

            // If hqIds is null, it means all HQs are blocked for that date
            if (blockedEntry.hqIds === null) return true;

            return blockedEntry.hqIds.includes(hqId);
        },
        isOutsideWorkingHours(date, hour, hqId) {
            const schedule = this.getHqScheduleForDate(date, hqId);
            if (!schedule) return true;

            const hourNumber = Number(hour.split(':')[0]);
            const openHour = Number(schedule.openHour);
            const closeHour = Number(schedule.closeHour);

            if ([hourNumber, openHour, closeHour].some(Number.isNaN)) {
                return true;
            }

            return hourNumber < openHour || hourNumber > closeHour;
        },
        isSlotBlocked(date, hour, room) {
            return this.isDayBlockedForHq(date, room.hqId) || this.isOutsideWorkingHours(date, hour, room.hqId);
        },
        actionEvent(date, hour, room) {
            const { id: roomId } = room;
            if (this.isSlotBlocked(date, hour, room)) {
                return; // Do nothing on blocked slots
            }
            
            const event = this.getEvent(date, hour, roomId);
            if (event) {
                this.selectedReservationId = event.id;
                this.editLocationVisible = true;
            } else {
                this.createLocationVisible = true;
                this.selectedDate = date;
                this.selectedHour = hour;
                this.selectedRoomId = roomId;
            }
        },
        openCreateLocationModal() {
            this.selectedDate = format(this.calendarDate || new Date(), 'dd/MM/yyyy');
            this.selectedHour = `${String(new Date().getHours()).padStart(2, '0')}:00`;
            this.selectedRoomId = this.selectedRoom ?? this.roomOptions[0]?.id ?? null;
            this.createLocationVisible = true;
        },
        getCalendarDates() {
            const daysOfWeek = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

            // Day mode
            if (this.calendarMode == 'day') {
                const day = this.calendarDate;

                return Array({
                    date: format(day, 'dd/MM/yyyy'),
                    text: `${daysOfWeek[day.getDay()]} - ${format(day, 'dd/MM')}`
                });
            }

            // Week mode
            const weekStart = startOfWeek(this.calendarDate, { weekStartsOn: 0 });

            // Generate days of week, without sunday
            const days = [];
            for (let i = 0; i < 7; i++) {
                const day = addDays(weekStart, i);
                if (day.getDay() == 0) continue;

                days.push({
                    date: format(day, 'dd/MM/yyyy'),
                    text: `${daysOfWeek[day.getDay()]} - ${format(day, 'dd/MM')}`
                });
            }

            return days;
        },
        getCalendarHours(startHour, endHour) {
            const hours = [];
            for (let h = startHour; h <= endHour; h++) {
                const formattedHour = String(h).padStart(2, '0') + ':00';
                hours.push(formattedHour);
            }
            return hours;
        },
        getCalendarTitle() {
            // Day mode
            if (this.calendarMode == 'day') {
                return this.capitalize(this.calendarDate.toLocaleString('pt-BR', { day: 'numeric', month: 'long' }));
            }

            // Week
            const weekStart = startOfWeek(this.calendarDate, { weekStartsOn: 0 });
            return this.capitalize(weekStart.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }));
        },
        getNextDate(direction) {
            const directionWeight = this.calendarMode === 'week' ? 7 : 1;

            let nextDate = addDays(this.calendarDate, direction * directionWeight);
            if (this.calendarMode == 'day' && nextDate.getDay() == 0) {
                nextDate = addDays(nextDate, direction * directionWeight);
            }

            return nextDate;
        },
        changeDate(direction) {
            // Calendar root variables
            this.calendarDate = this.getNextDate(direction);
            this.calendarDates = this.getCalendarDates();
            this.calendarTitle = this.getCalendarTitle();

            this.$emit('update:calendarDate', this.calendarDate);
        },
        showMarker(date, hour) {
            // Input
            const now = new Date();

            const [day, month, year] = date.split('/');
            const targetDate = new Date(year, month - 1, day);

            // Comparation
            const isSameDay =
                now.getFullYear() === targetDate.getFullYear() &&
                now.getMonth() === targetDate.getMonth() &&
                now.getDate() === targetDate.getDate();

            const isSameHour = now.getHours().toString() == hour.split(':')[0];

            return isSameDay && isSameHour;
        },
        getStartDate() {
            let now = new Date();
            if (this.calendarMode == 'day' && now.getDay() == 0) {
                return addDays(now, 1);
            }

            return now;
        },
        buildCalendar() {
            // If parent provided a controlDate, prefer it
            if (this.controlDate) {
                try {
                    this.calendarDate = (typeof this.controlDate === 'string') ? parseISO(this.controlDate) : this.controlDate;
                } catch (e) {
                    this.calendarDate = this.getStartDate();
                }
            } else {
                this.calendarDate = this.getStartDate();
            }
            this.calendarDates = this.getCalendarDates();
            this.calendarHours = this.getCalendarHours(7, 20);
            this.calendarTitle = this.getCalendarTitle();
        },
        // Fetch methods (From API)
        fetchHqList() {
            HeadquartersService.getHeadquarters()
                .then(response => {
                    const hqData = response.data.data;
                    this.headquartersOptions = hqData.map(hq => {
                        return {
                            id: hq.id,
                            name: hq.name,
                            schedules: Array.isArray(hq.schedules) ? hq.schedules : [],
                            rooms: hq.roomsDetailed.map(room => {
                                return {
                                    id: room.id,
                                    name: room.name,
                                    color: room.color,
                                }
                            })
                        }
                    });
                    
                    // Set first hq as selected
                    const defaultHqName = this.userData?.defaultHQ ?? null;
                    const normalize = s => s ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim() : '';

                    if (defaultHqName) {
                        const matched = this.headquartersOptions.find(hq => normalize(hq.name) === normalize(defaultHqName));
                        this.selectedHq = matched ? matched.id : (this.headquartersOptions[0]?.id ?? null);
                    } else {
                        this.selectedHq = this.headquartersOptions[0]?.id ?? null;
                    }
                    // If parent requested an HQ before we loaded the list, apply it now
                    if (this.controlHq) {
                        const desired = String(this.controlHq);
                        const matchedById = this.headquartersOptions.find(hq => String(hq.id) === desired);
                        if (matchedById) {
                            this.selectedHq = matchedById.id;
                        }
                    }
                    if (this.pendingControlHq) {
                        const desiredPending = String(this.pendingControlHq);
                        const matchedPending = this.headquartersOptions.find(hq => String(hq.id) === desiredPending);
                        if (matchedPending) {
                            this.selectedHq = matchedPending.id;
                            this.pendingControlHq = null;
                        }
                    }
                    this.onHqChange();
                })
                .catch(error => {
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        formatDate(dateStr, hourStr) {
            const [day, month, year] = dateStr.split('/');
            const [hours, minutes] = hourStr.split(':');

            return new Date(year, month - 1, day, hours, minutes);
        },
    },
    mounted() {
        this.buildCalendar();

        setInterval(() => {
            this.currentMinute = new Date().getMinutes();
        }, 60000);
    },
    watch: {
        controlDate(newVal) {
            if (newVal) {
                try {
                    this.calendarDate = (typeof newVal === 'string') ? parseISO(newVal) : newVal;
                    this.calendarDates = this.getCalendarDates();
                    this.calendarTitle = this.getCalendarTitle();
                } catch (e) {
                    console.warn('Invalid controlDate provided to Calendar:', newVal);
                }
            }
        },
        controlHq(newVal) {
            if (newVal) {
                // If HQs are loaded, apply immediately
                const matched = this.headquartersOptions.find(hq => String(hq.id) === String(newVal));
                if (matched) {
                    this.selectedHq = matched.id;
                    this.onHqChange();
                } else {
                    // Save pending to apply after HQ list loads
                    this.pendingControlHq = newVal;
                }
            }
        },
        controlDate(newVal) {
            if (newVal) {
                try {
                    this.calendarDate = (typeof newVal === 'string') ? parseISO(newVal) : newVal;
                    this.calendarDates = this.getCalendarDates();
                    this.calendarTitle = this.getCalendarTitle();
                } catch (e) {
                    console.warn('Invalid controlDate provided to Calendar:', newVal);
                }
            }
        },
        calendarMode() {
            this.buildCalendar();
        },
        // If userData is populated after the component mounted, fetch headquarters to apply user's default
        userData: {
            handler(newVal) {
                if (newVal) {
                    this.fetchHqList();
                }
            },
            immediate: false,
            deep: false
        }
    }
}
</script>

<style scoped>
/* Table  */
table {
    min-width: 100%;
    overflow-y: auto;
    table-layout: fixed;
    text-align: center;
}

/* Make the table area vertically scroll within the viewport
   while the header stays stuck to the top of the page */
.table-container {
    max-height: calc(100vh - 180px); /* header + filters approximate height */
}

tr,
th,
td {
    border: 1px solid rgb(108, 85, 85);
}

th, tr .header-cell {
    background-color: #ff5555;
    color: #ffffff;

    padding: 8px;
    position: sticky;
    z-index: 5;
    border: 1px solid rgb(108, 85, 85);
    box-shadow: 0 1px 0 0 rgb(108, 85, 85), 0 -1px 0 0 rgb(108, 85, 85);
}

thead tr:first-child th {
    top: 0;
}

thead tr:first-child th::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background-color: rgb(108, 85, 85);
    z-index: 10;
}

thead tr:nth-child(2) th {
    top: 41px; /* positioned below the first header row */
}

thead tr:nth-child(2) th::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background-color: rgb(108, 85, 85);
    z-index: 10;
}

td {
    width: 60px;
    max-width: 60px;
    height: 30px;

    position: relative;
    user-select: none;
}

td:hover {
    background-color: #fee6e6;
    cursor: pointer;
}

/* Sticky first column */
.sticky-col {
    position: sticky;
    left: 0;
    background-color: #ff5555;
    box-shadow: 1px 0 0 0 rgb(108, 85, 85), -1px 0 0 0 rgb(108, 85, 85);
}

/* Sticky corner cells (Data and Salas headers) - both vertical and horizontal */
thead .sticky-col {
    z-index: 20; /* Highest to stay on top of everything */
}

/* First row sticky corner */
thead tr:first-child .sticky-col {
    top: 0;
}

thead tr:first-child .sticky-col::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background-color: rgb(108, 85, 85);
    z-index: 10;
}

/* Second row sticky corner */
thead tr:nth-child(2) .sticky-col {
    top: 41px;
}

/* Second row sticky corner */
thead tr:nth-child(2) .sticky-col::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background-color: rgb(108, 85, 85);
    z-index: 10;
}

tbody .sticky-col {
    background-color: #ffffff;
    z-index: 15; /* Higher than regular header rows */
}

tbody .sticky-col:hover {
    background-color: #fee6e6;
}

/* Others */
.event {
    margin: 2px;
    padding: 4px;
    height: 90%;
    font-size: 12px;

    border-radius: 10px;
    background-color: #DFE0E2;

    text-align: start;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.event-others{
    background-color: #ededee;
    opacity: 0.5;
}
.event-others:hover{
    cursor: not-allowed;
}
.event-others span {
    display: block;
    text-align: center;
    width: 100%;
}

.marker {
    position: absolute;
    left: 0;
    right: 0;
    height: 3px;
    background-color: red;
}

.marker-circle::before {
    content: '';
    position: absolute;
    background-color: red;
    z-index: 1;

    left: -5px;
    top: -3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.past-time {
    background-color: #e2e8f0b0;
}
</style>